import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

// Create the auth context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Helper to normalize role casing to lowercase for consistency
const normalizeUserRoles = (user) => {
  if (!user || typeof user !== 'object') return user;
  const role = user.role ? String(user.role).toLowerCase() : undefined;
  const roles = Array.isArray(user.roles) ? user.roles.map(r => String(r).toLowerCase()) : (role ? [role] : []);
  return { ...user, role, roles };
};

// Normalize identity fields so UI can consistently display name and avatar
const normalizeUserIdentity = (user) => {
  if (!user || typeof user !== 'object') return user;
  const name = user.name || user.fullName || user.displayName || user.username || user.email || 'User';
  const avatar = user.avatar || user.avatarUrl || user.profilePic || user.photoURL || null;
  return { ...user, name, avatar };
};

// Decode a JWT token's payload safely
const decodeJwt = (token) => {
  try {
    const base64 = token.split('.')[1];
    const payload = JSON.parse(atob(base64.replace(/-/g, '+').replace(/_/g, '/')));
    return payload || {};
  } catch (e) {
    return {};
  }
};

const isTokenExpired = (token) => {
  if (!token) return true;
  const { exp } = decodeJwt(token);
  if (!exp) return false; // if no exp claim, assume not expired
  const nowSec = Math.floor(Date.now() / 1000);
  return exp <= nowSec;
};

// Synchronously read auth from storage (runs once during useState init — no blank screen)
const loadAuthFromStorage = () => {
  const tryParse = (storage) => {
    try {
      const u = storage.getItem('user');
      const t = storage.getItem('token');
      if (u && t) {
        return { user: normalizeUserIdentity(normalizeUserRoles(JSON.parse(u))), token: t };
      }
    } catch {}
    return null;
  };
  return tryParse(localStorage) || tryParse(sessionStorage) || null;
};

// Check if URL contains a token param that needs async processing
// Exclude /activate path — that token is for account activation, not login
const urlHasAuthToken = () => {
  try {
    if (window.location.pathname.startsWith('/activate')) return false;
    const params = new URLSearchParams(window.location.search);
    return !!(params.get('t') || params.get('token'));
  } catch { return false; }
};

// Provider component that wraps the app and makes auth object available to any child component that calls useAuth()
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const data = loadAuthFromStorage();
    if (data && !isTokenExpired(data.token)) return data.user;
    return null;
  });
  const [token, setToken] = useState(() => {
    const data = loadAuthFromStorage();
    if (data && !isTokenExpired(data.token)) return data.token;
    return null;
  });
  // Only block rendering if there's a URL token that needs async fetch
  const [loading, setLoading] = useState(() => urlHasAuthToken());
  const expiryTimerRef = useRef(null);

  const clearExpiryTimer = () => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
  };

  const scheduleAutoLogout = (jwt) => {
    clearExpiryTimer();
    if (!jwt) return;
    const { exp } = decodeJwt(jwt);
    if (!exp) return; // no expiry info
    const msLeft = exp * 1000 - Date.now();
    if (msLeft <= 0) {
      // already expired
      handleExpiredToken();
      return;
    }
    expiryTimerRef.current = setTimeout(() => {
      handleExpiredToken();
    }, msLeft + 500); // small buffer
  };

  const handleExpiredToken = () => {
    // Clear storage and redirect to login
    try {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('token');
    } catch {}
    setUser(null);
    setToken(null);
    // Redirect to login, preserving current path via URL param if needed
    const currentPath = window.location.pathname;
    const url = currentPath && currentPath !== '/login'
      ? `/login?from=${encodeURIComponent(currentPath)}`
      : '/login';
    window.location.replace(url);
  };

  // On mount: schedule auto-logout, handle URL token (async), and run migration
  useEffect(() => {
    const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/+$/, '');

    // Schedule auto-logout for the token loaded synchronously from storage
    const stored = loadAuthFromStorage();
    if (stored && !isTokenExpired(stored.token)) {
      scheduleAutoLogout(stored.token);
    } else if (stored && isTokenExpired(stored.token)) {
      handleExpiredToken();
      return;
    }

    // Handle URL token consumption (only async path)
    // Skip for /activate pages — those tokens are for account activation, not login
    const tryConsumeTokenFromUrl = async () => {
      try {
        if (window.location.pathname.startsWith('/activate')) return;
        const url = new URL(window.location.href);
        const urlToken = url.searchParams.get('t') || url.searchParams.get('token');
        const hasStored = !!(localStorage.getItem('token') || sessionStorage.getItem('token'));

        if (urlToken && !hasStored) {
          try { localStorage.setItem('token', urlToken); } catch {}
          try { sessionStorage.setItem('token', urlToken); } catch {}
          try {
            const res = await fetch(`${API_BASE}/auth/me`, { headers: { 'Authorization': `Bearer ${urlToken}` } });
            if (res.ok) {
              const u = await res.json();
              const normalized = normalizeUserIdentity(normalizeUserRoles(u));
              try { localStorage.setItem('user', JSON.stringify(normalized)); } catch {}
              try { sessionStorage.setItem('user', JSON.stringify(normalized)); } catch {}
              setUser(normalized);
              setToken(urlToken);
              scheduleAutoLogout(urlToken);
            }
          } catch (_) {}
        }

        // Always remove token from the URL
        if (urlToken) {
          try {
            url.searchParams.delete('t');
            url.searchParams.delete('token');
            const qs = url.searchParams.toString();
            const next = url.pathname + (qs ? `?${qs}` : '') + url.hash;
            window.history.replaceState({}, document.title, next);
          } catch {}
        }
      } catch {}
    };

    (async () => {
      if (urlHasAuthToken()) {
        await tryConsumeTokenFromUrl();
        setLoading(false);
      }

      // Migration: mirror sessionStorage auth to localStorage if missing
      try {
        const hasLocal = !!(localStorage.getItem('user') && localStorage.getItem('token'));
        const hasSession = !!(sessionStorage.getItem('user') && sessionStorage.getItem('token'));
        if (!hasLocal && hasSession) {
          localStorage.setItem('user', sessionStorage.getItem('user'));
          localStorage.setItem('token', sessionStorage.getItem('token'));
        }
      } catch {}
    })();

    return () => clearExpiryTimer();
  }, []);

  // Login function
  // Persist to BOTH sessionStorage and localStorage to ensure new tabs/windows stay authenticated.
  // This mirrors the auth state across tabs so opening a shared call link does not appear logged out.
  const login = (userData, authToken, remember = true) => {
    const normalized = normalizeUserIdentity(normalizeUserRoles(userData));

    // Always write to sessionStorage
    try {
      sessionStorage.setItem('user', JSON.stringify(normalized));
      sessionStorage.setItem('token', authToken);
    } catch (e) {
      // ignore
    }

    // Also mirror to localStorage for cross-tab availability
    try {
      localStorage.setItem('user', JSON.stringify(normalized));
      localStorage.setItem('token', authToken);
    } catch (e2) {
      // If localStorage fails (quota, privacy mode), we already wrote to sessionStorage
      // so the current tab remains authenticated.
      // eslint-disable-next-line no-console
      console.warn('Auth: failed to mirror auth to localStorage; relying on sessionStorage only.', e2);
    }

    // Optionally remember flag can be stored for future decisions
    try {
      localStorage.setItem('auth.remember', remember ? '1' : '0');
    } catch {}

    setUser(normalized);
    setToken(authToken);
    scheduleAutoLogout(authToken);
  };

  // Logout function
  const logout = () => {
    clearExpiryTimer();
    try {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('token');
    } catch {}
    setUser(null);
    setToken(null);
    // Redirect to home so it shows without sidebar when logged out
    window.location.href = '/';
  };

  // Refresh user from server (fetch /auth/me and merge with stored data)
  const refreshUser = async () => {
    try {
      const t = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!t) return;
      const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/+$/, '');
      const res = await fetch(`${API_BASE}/auth/me`, { headers: { 'Authorization': `Bearer ${t}`, 'Cache-Control': 'no-cache' } });
      if (!res.ok) return;
      const data = await res.json();
      const cur = localStorage.getItem('user') || sessionStorage.getItem('user');
      const curObj = cur ? JSON.parse(cur) : null;
      const merged = normalizeUserIdentity(normalizeUserRoles({ ...(curObj || {}), ...data }));
      const storage = localStorage.getItem('token') ? localStorage : (sessionStorage.getItem('token') ? sessionStorage : localStorage);
      storage.setItem('user', JSON.stringify(merged));
      setUser(merged);
      try { window.dispatchEvent(new CustomEvent('authUserUpdated', { detail: { user: merged } })); } catch (_) {}
    } catch (_) {
      // ignore transient errors
    }
  };

  // Periodic refresh + visibility listener
  useEffect(() => {
    if (loading) return; // wait for initial load to finish

    const onVisibility = () => { if (document.visibilityState === 'visible') { refreshUser(); } };
    document.addEventListener('visibilitychange', onVisibility);

    const interval = setInterval(refreshUser, 60 * 1000);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(interval);
    };
  }, [loading]);

  // Listen for external user updates (same-tab custom events and cross-tab storage changes)
  useEffect(() => {
    const applyUserFromStorage = () => {
      try {
        const src = localStorage.getItem('user') ? localStorage : (sessionStorage.getItem('user') ? sessionStorage : null);
        if (!src) return;
        const uRaw = src.getItem('user');
        const tRaw = src.getItem('token');
        if (!uRaw) return;
        const parsed = JSON.parse(uRaw);
        setUser(normalizeUserIdentity(normalizeUserRoles(parsed)));
        if (tRaw) setToken(tRaw);
      } catch (e) {
        // ignore parse errors
      }
    };

    const onAuthUserUpdated = (ev) => {
      try {
        // Prefer detail payload if provided, else read from storage
        const detailUser = ev && ev.detail && ev.detail.user ? ev.detail.user : null;
        if (detailUser) {
          const normalized = normalizeUserIdentity(normalizeUserRoles(detailUser));
          setUser(normalized);
          // persist back to where it was stored
          try {
            const storage = localStorage.getItem('user') ? localStorage : (sessionStorage.getItem('user') ? sessionStorage : localStorage);
            storage.setItem('user', JSON.stringify(normalized));
          } catch {}
        } else {
          applyUserFromStorage();
        }
      } catch {}
    };

    const onStorage = (e) => {
      if (!e) return;
      if (e.key === 'user' || e.key === 'token') {
        applyUserFromStorage();
      }
    };

    window.addEventListener('authUserUpdated', onAuthUserUpdated);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('authUserUpdated', onAuthUserUpdated);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Expose a small helper to update user in-place and persist
  const updateUser = (partialOrFull) => {
    setUser(prev => {
      const raw = typeof partialOrFull === 'function' ? partialOrFull(prev) : { ...prev, ...partialOrFull };
      const next = normalizeUserIdentity(normalizeUserRoles(raw));
      try {
        const storage = localStorage.getItem('user') ? localStorage : (sessionStorage.getItem('user') ? sessionStorage : localStorage);
        storage.setItem('user', JSON.stringify(next));
      } catch {}
      try { window.dispatchEvent(new CustomEvent('authUserUpdated', { detail: { user: next } })); } catch {}
      return next;
    });
  };

  // Context value
  const value = {
    user,
    token,
    loading,
    login,
    logout,
    updateUser,
    refreshUser,
    isAuthenticated: !!user && !!token && !isTokenExpired(token)
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};